/**
 * ExternalRequestDetail – detaljvisning av én ekstern samarbeid-henvendelse.
 * Åpnes fra varsel eller fra ekstern-innboksen.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Copy, ExternalLink, CalendarX, CalendarCheck, Send, CheckCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import i18next, { t as T } from "i18next";

const DATE_LOCALES = { no: "nb-NO", en: "en-GB", sv: "sv-SE", da: "da-DK" };
const loc = () => DATE_LOCALES[(i18next.language || "no").slice(0, 2)] || "nb-NO";

const PLATFORM_LINK = {
  telegram: (h) => `https://t.me/${h.replace(/^@/, "")}`,
  snapchat: (h) => `https://www.snapchat.com/add/${h.replace(/^@/, "")}`,
  instagram: (h) => `https://instagram.com/${h.replace(/^@/, "")}`,
  x: (h) => `https://x.com/${h.replace(/^@/, "")}`,
  tiktok: (h) => `https://tiktok.com/@${h.replace(/^@/, "")}`,
  reddit: (h) => `https://reddit.com/user/${h.replace(/^@/, "")}`,
  bluesky: (h) => `https://bsky.app/profile/${h.replace(/^@/, "")}`,
  onlyfans: (h) => `https://onlyfans.com/${h.replace(/^@/, "")}`,
  fansly: (h) => `https://fansly.com/${h.replace(/^@/, "")}`,
};

const PLATFORM_LABEL = {
  telegram: "Telegram", snapchat: "Snapchat", instagram: "Instagram",
  x: "X / Twitter", tiktok: "TikTok", reddit: "Reddit", bluesky: "Bluesky",
  onlyfans: "OnlyFans", fansly: "Fansly",
};

export default function ExternalRequestDetail() {
  const { user, loading: authLoading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState({});
  const [slotBusy, setSlotBusy] = useState(null); // null=ukjent, true=opptatt, false=ledig/ikke slot

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/collab/external-messages/${id}`);
        if (!cancelled) setMsg(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || T("ext_req.not_found_toast"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, id]);

  const refreshSlotState = async (date) => {
    if (!date) return;
    try {
      const r = await api.get(`/collab/calendar/me?start=${date}&end=${date}`);
      const s = (r.data?.slots || []).find((x) => x.date === date);
      setSlotBusy(s ? !s.is_available : false);
    } catch { setSlotBusy(null); }
  };

  useEffect(() => {
    if (msg?.slot_date) refreshSlotState(msg.slot_date);
    else setSlotBusy(null);
  }, [msg?.slot_date]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_creator && !["admin", "owner"].includes(user.role)) {
    return <Navigate to="/skaper/samarbeid" replace />;
  }

  const platformKey = (msg?.external_platform || "").toLowerCase();
  const contactUrl = msg?.external_handle && PLATFORM_LINK[platformKey]
    ? PLATFORM_LINK[platformKey](msg.external_handle)
    : null;
  const platformLabel = PLATFORM_LABEL[platformKey] || (platformKey === "other" ? T("ext_req.other") : (msg?.external_platform || "").toUpperCase());

  const copyHandle = () => {
    if (!msg?.external_handle) return;
    navigator.clipboard.writeText(msg.external_handle);
    toast.success(T("ext_req.copied", { h: msg.external_handle }));
  };

  const markSlotBusy = async () => {
    if (!msg?.slot_date || !msg?.slot_type) return;
    setBusy(true);
    try {
      await api.post("/collab/calendar/me", {
        date: msg.slot_date,
        is_available: false,
        types: [msg.slot_type],
        platforms: [],
        account_ids: [],
        duration_days: 1,
        note: `Booket via ekstern henvendelse (@${msg.external_handle})`,
      });
      toast.success(T("ext_req.marked_busy"));
      setSlotBusy(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ext_req.err_calendar"));
    } finally {
      setBusy(false);
    }
  };

  const releaseSlot = async () => {
    if (!msg?.slot_date) return;
    if (!window.confirm(T("ext_req.confirm_release"))) return;
    setBusy(true);
    try {
      const r = await api.post(`/collab/external-messages/${id}/release-date`);
      toast.success(T("ext_req.released", { d: r.data?.date_freed || "" }));
      // Refresh henvendelsen — den har ikke lenger slot_date
      const fresh = await api.get(`/collab/external-messages/${id}`);
      setMsg(fresh.data);
      setSlotBusy(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ext_req.err_release"));
    } finally {
      setBusy(false);
    }
  };

  const togglePosted = async (side) => {
    setBusy(true);
    try {
      const r = await api.post(`/collab/external-messages/${id}/toggle-posted`, { side });
      setMsg({
        ...msg,
        posted_by_creator: r.data.posted_by_creator,
        posted_by_partner: r.data.posted_by_partner,
        posted_by_creator_at: r.data.posted_by_creator_at,
        posted_by_partner_at: r.data.posted_by_partner_at,
      });
      const ts = new Date().toLocaleTimeString(loc());
      setSavedFlash((prev) => ({ ...prev, [side]: ts }));
      setTimeout(() => {
        setSavedFlash((prev) => {
          const next = { ...prev };
          delete next[side];
          return next;
        });
      }, 3500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ext_req.err_update"));
    } finally {
      setBusy(false);
    }
  };

  const markHandled = async () => {
    setBusy(true);
    try {
      await api.post(`/collab/external-messages/${id}/mark-handled`);
      toast.success(T("ext_req.marked_handled"));
      navigate("/skaper/samarbeid?tab=external-inbox");
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ext_req.err_update"));
    } finally {
      setBusy(false);
    }
  };

  const markUnhandled = async () => {
    setBusy(true);
    try {
      await api.post(`/collab/external-messages/${id}/mark-unhandled`);
      setMsg({ ...msg, handled: false });
      toast.success(T("ext_req.undone"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ext_req.err_update"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Layout><div className="max-w-2xl mx-auto px-4 py-12 text-center text-[var(--cream-soft)]">{T("ext_req.loading")}</div></Layout>;
  }
  if (!msg) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="ext-req-notfound">
          <h1 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">{T("ext_req.not_found")}</h1>
          <Link to="/skaper/samarbeid?tab=external-inbox" className="btn-magenta">
            {T("ext_req.to_inbox")}
          </Link>
        </div>
      </Layout>
    );
  }

  const dateFmt = msg.slot_date
    ? new Date(msg.slot_date).toLocaleDateString(loc(), { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : null;
  const releasedFromFmt = msg.released_from_date
    ? new Date(msg.released_from_date).toLocaleDateString(loc(), { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : null;

  const bothPosted = msg.posted_by_creator && msg.posted_by_partner;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="ext-req-page">
        <div className="mb-6">
          <Link to="/skaper/samarbeid?tab=external-inbox"
            className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
            data-testid="ext-req-back">
            <ArrowLeft className="h-4 w-4" /> {T("ext_req.to_inbox")}
          </Link>
        </div>

        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">
          {T("ext_req.external_request")} {msg.handled && T("ext_req.handled_tag")}
        </div>
        <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-6">
          @{msg.external_handle}
        </h1>

        {/* Kontaktinfo */}
        <div className="panel-tight p-4 mb-4" data-testid="ext-req-contact">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--gold)] mb-2">{T("ext_req.contact_on")}</div>
          <div className="flex items-center flex-wrap gap-2">
            <span className="font-fraunces italic text-xl text-[var(--cream)]">{platformLabel}</span>
            <span className="text-[var(--cream-soft)]">·</span>
            <span className="font-manrope text-[var(--cream)]">@{msg.external_handle}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={copyHandle} type="button"
              className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="ext-req-copy">
              <Copy className="h-3 w-3" /> {T("ext_req.copy_handle")}
            </button>
            {contactUrl && (
              <a href={contactUrl} target="_blank" rel="noopener noreferrer"
                className="btn-magenta text-xs inline-flex items-center gap-1.5" data-testid="ext-req-open">
                <ExternalLink className="h-3 w-3" /> {T("ext_req.open_in", { p: platformLabel })}
              </a>
            )}
          </div>
        </div>

        {/* Ønsket dag + type — vises kun hvis dato fortsatt er koblet */}
        {msg.slot_date && msg.slot_type && (
          <div className="panel-tight p-4 mb-4" data-testid="ext-req-slot">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">{T("ext_req.wanted_day")}</div>
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <span className="font-meta text-xs px-2 py-1 border border-[var(--magenta)] text-[var(--magenta)] uppercase tracking-wider">
                {msg.slot_type.toUpperCase()}
              </span>
              <span className="font-fraunces italic text-lg text-[var(--cream)]">{dateFmt}</span>
            </div>
            <button onClick={markSlotBusy} disabled={busy || slotBusy === true} type="button"
              className="btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="ext-req-mark-busy">
              <CalendarX className="h-3 w-3" />
              {slotBusy === true ? T("ext_req.already_busy") : T("ext_req.mark_busy")}
            </button>
            {slotBusy === true && (
              <button onClick={releaseSlot} disabled={busy} type="button"
                className="btn-ghost text-xs inline-flex items-center gap-1.5 ml-2 border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 disabled:opacity-40"
                data-testid="ext-req-release-slot">
                <CalendarCheck className="h-3 w-3" /> {T("ext_req.release_date")}
              </button>
            )}
            <p className="text-[10px] font-meta text-[var(--cream-faint)] mt-2">
              {slotBusy === true
                ? T("ext_req.busy_hint")
                : T("ext_req.free_hint")}
            </p>
          </div>
        )}

        {/* Frigitt-dato-info (vises hvis datoen er blitt sluppet) */}
        {!msg.slot_date && msg.released_from_date && (
          <div className="panel-tight p-4 mb-4 border-[var(--gold)]/40 bg-[var(--gold)]/5" data-testid="ext-req-released">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--gold)] mb-2">{T("ext_req.date_released")}</div>
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <CalendarCheck className="h-4 w-4 text-[var(--gold)]" />
              <span className="font-fraunces italic text-base text-[var(--cream)]">{releasedFromFmt}</span>
              {msg.released_from_type && (
                <span className="font-meta text-[10px] px-2 py-0.5 border border-[var(--gold)]/60 text-[var(--gold)] uppercase tracking-wider">
                  {msg.released_from_type.toUpperCase()}
                </span>
              )}
            </div>
            <p className="text-[11px] font-meta text-[var(--cream-faint)]">
              {T("ext_req.released_hint")}
            </p>
          </div>
        )}

        {/* Status på avtalen — postet av begge? */}
        <div className="panel-tight p-4 mb-4" data-testid="ext-req-posted-status">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--gold)]">{T("ext_req.deal_status")}</div>
            {bothPosted && (
              <span className="inline-flex items-center gap-1 text-xs font-meta tracking-wider text-[#7BBF6E]">
                <CheckCheck className="h-3.5 w-3.5" /> {T("ext_req.posted_both")}
              </span>
            )}
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--cream)]">
              <input
                type="checkbox"
                checked={!!msg.posted_by_creator}
                onChange={() => togglePosted("creator")}
                disabled={busy}
                className="w-4 h-4 accent-[var(--magenta)] cursor-pointer"
                data-testid="ext-req-posted-me"
              />
              <span className="flex-1">
                {T("ext_req.posted_me")}
                {msg.posted_by_creator_at && (
                  <span className="ml-2 text-[10px] font-meta text-[var(--cream-faint)]">
                    ({new Date(msg.posted_by_creator_at).toLocaleString(loc())})
                  </span>
                )}
                {savedFlash.creator && (
                  <span className="ml-2 text-[10px] font-meta text-[#46B85B]">
                    {T("ext_req.saved", { t: savedFlash.creator })}
                  </span>
                )}
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--cream)]">
              <input
                type="checkbox"
                checked={!!msg.posted_by_partner}
                onChange={() => togglePosted("partner")}
                disabled={busy}
                className="w-4 h-4 accent-[var(--magenta)] cursor-pointer"
                data-testid="ext-req-posted-partner"
              />
              <span className="flex-1">
                {T("ext_req.posted_partner", { h: msg.external_handle })}
                {msg.posted_by_partner_at && (
                  <span className="ml-2 text-[10px] font-meta text-[var(--cream-faint)]">
                    ({new Date(msg.posted_by_partner_at).toLocaleString(loc())})
                  </span>
                )}
                {savedFlash.partner && (
                  <span className="ml-2 text-[10px] font-meta text-[#46B85B]">
                    {T("ext_req.saved", { t: savedFlash.partner })}
                  </span>
                )}
              </span>
            </label>
          </div>
          <p className="text-[10px] font-meta text-[var(--cream-faint)] mt-3">
            {T("ext_req.posted_hint")}
          </p>
        </div>

        {/* Melding */}
        <div className="panel-tight p-4 mb-4" data-testid="ext-req-message">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">{T("ext_req.message")}</div>
          <p className="text-[var(--cream)] font-manrope whitespace-pre-line text-sm leading-relaxed">
            {msg.message}
          </p>
          <p className="text-[10px] font-meta text-[var(--cream-faint)] mt-3">
            {T("ext_req.received", { d: new Date(msg.created_at).toLocaleString(loc()) })}
          </p>
        </div>

        {/* Håndter-knapper */}
        <div className="flex flex-wrap gap-2 mt-6">
          {msg.handled ? (
            <button onClick={markUnhandled} disabled={busy} type="button"
              className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="ext-req-unhandle">
              <X className="h-4 w-4" /> {T("ext_req.undo_handled")}
            </button>
          ) : (
            <button onClick={markHandled} disabled={busy} type="button"
              className="btn-magenta inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="ext-req-mark-handled">
              <Check className="h-4 w-4" /> {T("ext_req.mark_handled")}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
